<script setup>
import Lucide from "@/base-components/Lucide";
import {useFetch} from "@/composables/useFetch.js";
import {onMounted, ref} from "vue";
import Vue3Datatable from "@bhplugin/vue3-datatable";
import "@bhplugin/vue3-datatable/dist/style.css";
import {useRoute, useRouter} from "vue-router";
import {uri} from "@/constants/config.js";
import "vue-awesome-paginate/dist/style.css";
import BasePagination from "@/base-components/Table/BasePagination.vue";
import {useI18n} from "vue-i18n";
import AuthItemDialog from "@/pages/manager/rbac/components/AuthItemDialog.vue";
import DeleteDialog from "@/base-components/Dialog/DeleteDialog.vue";

const props = defineProps({authType: {type: String, default: false}});

const {t: $t} = useI18n()
const route = useRoute()
const router = useRouter()
const dataList = ref([])
const showDialogFlag = ref(false)
const showDeleteFlag = ref(false)
const isUpdateFlag = ref(false)
const isLoadingFlag = ref(true)
const currentItem = ref(undefined)
const cols = [
  {field: "counter", title: "#"},
  {field: "name", title: $t('title.name')},
  {field: "setting", title: $t('title.setting')},
]
const pageOptions = ref({
  count: 1,
  limit: 10,
  page: 1
})


const loadList = async () => {
  isLoadingFlag.value = true
  const url = uri.admin.rbac[props.authType].list
  try {
    const {
      data,
      error
    } = await useFetch(`${url}?page=${pageOptions.value.page}`, {method: "GET"});
    if (!error) {
      dataList.value = data.data
      pageOptions.value.count = data.meta.total
      pageOptions.value.limit = data.meta.perPage
    } else {
      console.error(error)
    }
  } catch (err) {
    console.log(err)
  } finally {
    isLoadingFlag.value = false
  }
}

async function deleteItemConfirm() {
  isLoadingFlag.value = true
  showDeleteFlag.value = false
  try {
    await useFetch(`${uri.admin.rbac.add}`, {params: {name: currentItem.value.name}, method: "DELETE"});
    loadList()
  } catch (e) {
    console.error(e)
  } finally {
    isLoadingFlag.value = false
  }

}


async function editItem(row) {
  isUpdateFlag.value = true
  currentItem.value = row
  showDialogFlag.value = true
}

async function deleteItem(row) {
  currentItem.value = row
  showDeleteFlag.value = true
}

async function addItem() {
  isUpdateFlag.value = false
  currentItem.value = {}
  showDialogFlag.value = true
}

async function childrenPage(row) {
  router.push({name: 'manager-rbac-assignment', params: {id:row.type.toLowerCase()}})
}

// Fetch data when the component mounts
onMounted(loadList);

</script>
<template>
  <section>
    <AuthItemDialog v-if="showDialogFlag"
                    @close="showDialogFlag = false"
                    @onSuccess="loadList"
                    :item="currentItem"
                    :is-update="isUpdateFlag"
                    :show="showDialogFlag"/>
    <DeleteDialog
        :close="() => showDeleteFlag = false"
        :open="showDeleteFlag"
        :confirm="deleteItemConfirm"
        :cancel="() => showDeleteFlag = false">
      {{ currentItem.name ?? '' }}
    </DeleteDialog>
    <div class="grid grid-cols-12 gap-6 mt-1">
      <div class="col-span-12 lg:col-span-12 2xl:col-span-12">
        <!-- BEGIN: Inbox Content -->
        <div class="mt-5 intro-y box">

          <div
              class="flex p-5 border-b sm:flex-row text-slate-500 border-slate-200/60 text-end w-full justify-end items-end"
          >
            <div class="w-5 h-5 ms-5 cursor-pointer" @click="loadList">
              <Lucide icon="RefreshCw" class="w-6 h-6"/>
            </div>
            <div class="w-5 h-5 ms-5 cursor-pointer" @click="addItem">
              <Lucide icon="PlusCircle" class="w-6 h-6"/>
            </div>
          </div>
          <div class="mb-5 relative">
          </div>

          <vue3-datatable
              :columns="cols"
              :rows="dataList"
              :loading="isLoadingFlag"
              :totalRows="pageOptions.count"
              :isServerMode="true"
              :pageSize="pageOptions.limit"
              :pagination="false"
              :sortable="false"
              class="advanced-table px-4 text-start items-start justify-start"
              rowClass="cursor-pointer"
          >
            <!-- Empty header slot to remove the header -->
            <template #header>
              <!-- No content here will hide the header -->
            </template>
            <!-- Empty footer slot to remove the footer -->
            <template #footer>
              <!-- No content here will hide the footer -->
            </template>

            <template #counter="data,key">
              <div class="text-start">
                {{
                  (dataList.indexOf(data.value) + 1) + ((pageOptions.page - 1) * pageOptions.limit)
                }}
              </div>
            </template>

            <template #department="data,key">
              <div class="text-start">
                {{ data.value.Department.name }}
              </div>
            </template>

            <template #name="data,key">
              <div class="text-start">
                {{ data.value.name }}
              </div>
            </template>

            <template #setting="data,key">
              <div class="text-start flex">
                <div class="ml-1"
                    @click="editItem(data.value)">
                  <Lucide icon="Edit" class="w-6 h-6 text-gray-700"/>
                </div>
                <div class="ml-1"
                    :title="$t('label.children')"
                    @click="childrenPage(data.value)">
                  <Lucide icon="ArchiveRestore" class="w-6 h-6 text-gray-700"/>
                </div>
                <div
                    @click="deleteItem(data.value)">
                  <Lucide icon="Trash" class="w-6 h-6 text-red-600"/>
                </div>
              </div>
            </template>

          </vue3-datatable>
          <div
              class="flex flex-col items-center p-5 text-center sm:flex-row sm:text-left text-slate-500"
          >
            <div>

              <BasePagination
                  v-if="pageOptions.count>pageOptions.limit"
                  :page-options="pageOptions"
                  @page-change="loadList"
              />
            </div>
            <div class="mt-2 sm:ml-auto sm:mt-0">
            </div>
          </div>
        </div>
        <!-- END: Inbox Content -->
      </div>
    </div>
  </section>
</template>
